/**
 * 全站路由配置
 *
 * 建议:
 * 1. 代码中路由统一使用name属性跳转(不使用path属性)
 */
import Vue from 'vue'
import Router from 'vue-router'
import { clearLoginInfo } from '@/utils'
import store from '@/store'
import menuApi from '@/api/sys-menu'
import ShippingPointMap from '@/static/shipping-point-map'

Vue.use(Router)

// 开发环境不使用懒加载, 因为懒加载页面太多的话会造成webpack热更新太慢, 所以只有生产环境使用懒加载
const _import = require('./import-' + process.env.NODE_ENV)

// 全局路由(无需嵌套上左右整体布局)
const globalRoutes = [
  {
    path: '/404',
    component: _import('common/404'),
    name: '404',
    meta: {
      title: '404未找到'
    }
  },
  {
    path: '/login',
    component: _import('common/login'),
    name: 'login',
    meta: {
      title: '登录'
    }
  },
  {
    path: '/timeline/:qrcode',
    component: _import('common/timeline'),
    name: 'timeline',
    meta: {
      title: '物流详情'
    }
  },
  {
    path: '/view',
    component: _import('common/file-view2'),
    name: 'view',
    meta: {
      title: '文件浏览'
    }
  },
  {
    path: '/qrcode/:qrcode/:size',
    component: _import('modules/shpt/qrcode2'),
    name: 'qrcode',
    meta: {
      title: 'Qrcode'
    }
  }
]

// 主入口路由(需嵌套上左右整体布局)
const mainRoutes = {
  path: '/',
  component: _import('main'),
  name: 'main',
  redirect: {
    name: 'home'
  },
  meta: {
    title: '主入口整体布局'
  },
  children: [
    // 通过meta对象设置路由展示方式
    // 1. isTab: 是否通过tab展示内容, true: 是, false: 否
    // 2. iframeUrl: 是否通过iframe嵌套展示内容, '以http[s]://开头': 是, '': 否
    // 提示: 如需要通过iframe嵌套展示内容, 但不通过tab打开, 请自行创建组件使用iframe处理!
    {
      path: 'home',
      component: _import('common/home'),
      name: 'home',
      meta: {
        title: '首页'
      }
    },
    {
      path: 'theme',
      component: _import('common/theme'),
      name: 'theme',
      meta: {
        title: '主题'
      }
    }
  ]
  // beforeEnter (to, from, next) {
  //   let token = Vue.cookie.get('token')
  //   if (!token || !/\S/.test(token)) {
  //     clearLoginInfo()
  //     next({name: 'login'})
  //   }
  //   next()
  // }
}

const router = new Router({
  mode: 'history',
  scrollBehavior: () => ({y: 0}),
  routes: globalRoutes.concat(mainRoutes)
})

// 添加动态(菜单)路由
// 1. 已经添加 or 全局路由, 直接访问
// 2. 获取菜单列表, 添加并保存本地存储
router.beforeEach((to, from, next) => {
  if (fnCurrentRouteType(to, globalRoutes) === 'global') {
    next()
    return
  }
  let token = Vue.cookie.get('token')
  if (!token || /^\s+$/.test(token)) {
    clearLoginInfo()
    next({name: 'login'})
    return
  }
  if (store.state.common.menuList.length > 0) {
    next()
    return
  }
  menuApi.getMenuNav().then(({data}) => {
    if (data && data.code === 0) {
      let _menuList = data.menuList
      fnAddDynamicMenuRoutes(_menuList)
      store.commit('common/updateMenuList', _menuList)
      sessionStorage.setItem('menuList', JSON.stringify(_menuList || '[]'))
      next({...to, replace: true})
    } else {
      sessionStorage.setItem('menuList', '[]')
      next()
    }
  }).catch((e) => {
    console.error(`%c${e} 请求菜单列表和权限失败, 跳转至登录页!`, 'color:blue', e)
    router.push({name: 'login'})
  })
})

/**
 * 判断当前路由类型, global: 全局路由, main: 主入口路由
 * @param {*} route 当前路由
 */
function fnCurrentRouteType (route, globalRoutes = []) {
  // let temp = []
  // for (let globalRoute of globalRoutes) {
  //   if (route.path === globalRoute.path) {
  //     return 'global'
  //   } else if (globalRoute.children && globalRoute.children.length >= 1) {
  //     temp = temp.concat(globalRoute.children)
  //   }
  // }
  // return temp.length >= 1 ? fnCurrentRouteType(route, temp) : 'main'
  for (let globalRoute of globalRoutes) {
    if (route.name === globalRoute.name) {
      return 'global'
    }
  }
  return 'main'
}

/**
 * 添加动态(菜单)路由
 * @param {*} menuList 菜单列表
 * @param {*} routes 递归创建的动态(菜单)路由
 */
function fnAddDynamicMenuRoutes (menuList = [], routes = []) {
  let temp = []
  for (let menu of menuList) {
    let url = menu.url
    if (menu.list && menu.list.length >= 1) {
      temp = temp.concat(menu.list)
    } else if (url && /\S/.test(url)) {
      url = url.replace(/^\//, '')
      let route = {
        path: url.replace('/', '-'),
        component: null,
        name: url.replace('/', '-'),
        meta: {
          menuId: menu.id,
          title: menu.name,
          isDynamic: true,
          isTab: true,
          iframeUrl: '',
          type: 0
        }
      }
      // url以http[s]://开头, 通过iframe展示
      // if (isURL(url)) {
      //   route['path'] = `i-${menu.id}`
      //   route['name'] = `i-${menu.id}`
      //   route['meta']['iframeUrl'] = url
      // } else {
      try {
        let component, type
        for (let [key, value] of ShippingPointMap.entries()) {
          if (key.test(url)) {
            type = value
            break
          }
        }
        if (/^bdc\/\S/.test(url)) {
          component = _import(`modules/bdc/data-center`)
        } else if (/^shpt\/shipment-file$/.test(url)) {
          component = _import(`modules/shpt/shipment-file`)
        } else if (/^shpt\/\S/.test(url)) {
          component = _import(`modules/shpt/shipment`)
        } else {
          component = _import(`modules/${url}`)
        }
        route['component'] = component
        route['meta']['type'] = type
      } catch (e) {
      }
      // }
      routes.push(route)
    }
  }
  if (temp.length >= 1) {
    fnAddDynamicMenuRoutes(temp, routes)
  } else {
    mainRoutes.children = routes
    router.addRoutes([
      mainRoutes,
      {path: '*', redirect: {name: '404'}}
    ])
    sessionStorage.setItem('dynamicMenuRoutes', JSON.stringify(routes || '[]'))
    // console.log('\n')
    // console.log('%c!<-------------------- 动态(菜单)路由 s -------------------->', 'color:blue')
    // console.log(routes)
    // console.log('%c!<-------------------- 动态(菜单)路由 e -------------------->', 'color:blue')
  }
}

export default router



// WEBPACK FOOTER //
// ./src/router/index.js